import { useRef, useState } from 'react';
import { useT } from '@kmgestao/i18n';
import { Button, toast } from '@kmgestao/ui';

import {
  LOGO_ALLOWED_MIME,
  LOGO_MAX_BYTES,
  useDeleteClinicLogo,
  useUploadClinicLogo,
} from '../../../features/clinic-appearance/queries.js';

/**
 * `ClinicLogoUploader` — UI de upload do logo da clínica (PR-M2-04b).
 *
 * Substitui o placeholder "Em breve" do PR-M2-04. Permite ao owner/admin:
 *   - Visualizar logo atual (se houver)
 *   - Fazer upload de novo (PNG / JPG / WEBP / SVG, max 2MB)
 *   - Remover (com confirmação)
 *
 * **Por que não usar `<Dropzone>` de lib externa**: regra §5.13 do
 * AGENTS.md — zero dep nova quando primitive nativo basta. `<input
 * type="file">` escondido + label como trigger visual cobre 100% do
 * caso de uso, é acessível por teclado e screen reader, e respeita
 * design tokens.
 *
 * **Por que validar MIME/size no client antes de mandar**: storage
 * rejeita uploads inválidos (file_size_limit + allowed_mime_types da
 * migration 0070), mas validar primeiro economiza upload-then-fail e
 * dá mensagem específica ao usuário (em vez de erro genérico do API).
 *
 * **Acessibilidade**:
 *   - Label do input file é o botão visual (clicável + foco visível)
 *   - aria-describedby liga o input às hints de tipos/tamanho
 *   - Imagem preview tem alt apropriado
 *   - Botão "remover" tem confirmação inline (sem dialog modal — é
 *     ação reversível: re-upload restaura)
 *
 * **Estados visuais**:
 *   - sem logo: card vazio com CTA upload + hints
 *   - com logo: preview + botões "trocar" e "remover"
 *   - durante upload: spinner via mutation isPending
 */
interface ClinicLogoUploaderProps {
  clinicId: string;
  /** URL pública atual do logo, ou null. Vem de `useClinicTheme`. */
  currentLogoUrl: string | null;
}

export function ClinicLogoUploader({ clinicId, currentLogoUrl }: ClinicLogoUploaderProps) {
  const { t } = useT('clinics');
  const inputRef = useRef<HTMLInputElement>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const uploadMutation = useUploadClinicLogo();
  const deleteMutation = useDeleteClinicLogo();
  const isBusy = uploadMutation.isPending || deleteMutation.isPending;

  const handleFilePick = async (file: File) => {
    // Guards client-side ANTES do upload — feedback rápido + mensagens
    // específicas. Storage também valida (defense in depth).
    if (!LOGO_ALLOWED_MIME.includes(file.type as (typeof LOGO_ALLOWED_MIME)[number])) {
      toast.error(t('appearance.logo.uploader.errors.invalidTypeTitle'), {
        description: t('appearance.logo.uploader.errors.invalidTypeDescription'),
      });
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error(t('appearance.logo.uploader.errors.tooLargeTitle'), {
        description: t('appearance.logo.uploader.errors.tooLargeDescription'),
      });
      return;
    }

    try {
      await uploadMutation.mutateAsync({ clinicId, file });
      toast.success(t('appearance.logo.uploader.feedback.uploadSuccessTitle'), {
        description: t('appearance.logo.uploader.feedback.uploadSuccessDescription'),
      });
    } catch (err) {
      toast.error(t('appearance.logo.uploader.feedback.uploadErrorTitle'), {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      // Reseta o input pra permitir re-upload do MESMO arquivo.
      // Sem isso, browser não dispara onChange se o user escolher o
      // mesmo file 2x seguidas.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ clinicId });
      toast.success(t('appearance.logo.uploader.feedback.deleteSuccessTitle'));
    } catch (err) {
      toast.error(t('appearance.logo.uploader.feedback.deleteErrorTitle'), {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setConfirmingDelete(false);
    }
  };

  return (
    <div className="space-y-4">
      {currentLogoUrl ? (
        <LogoPreviewState
          logoUrl={currentLogoUrl}
          isBusy={isBusy}
          confirmingDelete={confirmingDelete}
          onTrigger={() => inputRef.current?.click()}
          onRequestDelete={() => setConfirmingDelete(true)}
          onCancelDelete={() => setConfirmingDelete(false)}
          onConfirmDelete={handleDelete}
        />
      ) : (
        <LogoEmptyState isBusy={isBusy} onTrigger={() => inputRef.current?.click()} />
      )}

      {/* Input escondido — disparado pelos botões visuais. accept= e
          MIME nos toasts ficam em sincronia com LOGO_ALLOWED_MIME. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="sr-only"
        aria-describedby="clinic-logo-hints"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFilePick(file);
        }}
      />

      <p id="clinic-logo-hints" className="text-xs text-muted-foreground">
        {t('appearance.logo.uploader.hints')}
      </p>
    </div>
  );
}

// =============================================================================
// Sub-componentes — separados pra deixar a função principal legível
// =============================================================================

interface LogoEmptyStateProps {
  isBusy: boolean;
  onTrigger: () => void;
}

function LogoEmptyState({ isBusy, onTrigger }: LogoEmptyStateProps) {
  const { t } = useT('clinics');
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center">
      <p className="text-sm font-medium text-foreground">
        {t('appearance.logo.uploader.empty.title')}
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        {t('appearance.logo.uploader.empty.description')}
      </p>
      <Button type="button" className="mt-4" onClick={onTrigger} disabled={isBusy}>
        {isBusy ? t('appearance.logo.uploader.uploading') : t('appearance.logo.uploader.empty.cta')}
      </Button>
    </div>
  );
}

interface LogoPreviewStateProps {
  logoUrl: string;
  isBusy: boolean;
  confirmingDelete: boolean;
  onTrigger: () => void;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}

function LogoPreviewState({
  logoUrl,
  isBusy,
  confirmingDelete,
  onTrigger,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}: LogoPreviewStateProps) {
  const { t } = useT('clinics');
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-center rounded-lg border border-border bg-card p-6">
        <img
          src={logoUrl}
          alt={t('appearance.logo.uploader.previewAlt')}
          className="max-h-32 max-w-full object-contain"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={onTrigger} disabled={isBusy}>
          {isBusy
            ? t('appearance.logo.uploader.uploading')
            : t('appearance.logo.uploader.preview.replace')}
        </Button>

        {confirmingDelete ? (
          <>
            <span className="text-sm text-muted-foreground">
              {t('appearance.logo.uploader.preview.confirmDelete')}
            </span>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onConfirmDelete}
              disabled={isBusy}
            >
              {isBusy
                ? t('appearance.logo.uploader.deleting')
                : t('appearance.logo.uploader.preview.confirmYes')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancelDelete}
              disabled={isBusy}
            >
              {t('appearance.logo.uploader.preview.confirmCancel')}
            </Button>
          </>
        ) : (
          <Button type="button" variant="ghost" onClick={onRequestDelete} disabled={isBusy}>
            {t('appearance.logo.uploader.preview.delete')}
          </Button>
        )}
      </div>
    </div>
  );
}
